import { Link } from "@inertiajs/react"
import { useTranslation } from "react-i18next"

import { Badge } from "@/components/ui/badge"
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { cn } from "@/lib/utils"
import { brandRewardsRedemptionsPath, rewardsPath } from "@/routes"

/**
 * OKS-61 — Page-level tab strip for the brand Rewards surfaces.
 *
 * Renders the Catalogue / Redemptions tabs at the top of both
 * `/brand/rewards` (Catalogue active) and `/brand/rewards/redemptions`
 * (Redemptions active). The tab strip is **visual-only**: actual
 * navigation is via Inertia `<Link>` so URL state stays the single
 * source of truth (per CLAUDE.md hard rule + UX-OQ-11).
 *
 * UX states:
 *   - `pendingCount > 0` → Redemptions tab carries a numeric Badge
 *     to the right of the label.
 *   - `pendingCount === 0` → NO badge (positive-space empty signal,
 *     UX-OQ-3, mirrors `<NavMain>` unread cap convention).
 *   - `pendingCount > 99` → badge renders `99+` cap.
 *
 * Accessibility:
 *   - Each tab pill is a real `<a>` (via `<Link>`), so keyboard-tab
 *     navigation + screen-reader semantics are inherited from the
 *     anchor element.
 *   - The pending count chip carries an `aria-label` so screen
 *     readers don't read just the number out of context.
 *
 * NOTE: The shadcn `Tabs` primitive requires a `value` prop on the
 * Root + `value` on each Trigger. We pass `activeTab` to the Root
 * so the active-state styling resolves, but onValueChange is omitted
 * — the Link inside each Trigger handles navigation; nobody mutates
 * Tabs state.
 */
interface RewardsTabStripProps {
  activeTab: "catalogue" | "redemptions"
  pendingCount: number
}

const PENDING_COUNT_CAP = 99

export function RewardsTabStrip({
  activeTab,
  pendingCount,
}: RewardsTabStripProps) {
  const { t } = useTranslation()

  const displayCount =
    pendingCount > PENDING_COUNT_CAP ? `${PENDING_COUNT_CAP}+` : pendingCount
  const showBadge = pendingCount > 0

  return (
    <Tabs value={activeTab} data-testid="rewards-tab-strip">
      <TabsList>
        <TabsTrigger
          value="catalogue"
          render={
            <Link
              href={rewardsPath()}
              data-testid="rewards-tab-catalogue"
              className={cn(
                "no-underline",
                activeTab === "catalogue" ? "pointer-events-none" : undefined,
              )}
            />
          }
        >
          {t("rewards.redemptions.tab.catalogue")}
        </TabsTrigger>
        <TabsTrigger
          value="redemptions"
          render={
            <Link
              href={brandRewardsRedemptionsPath()}
              data-testid="rewards-tab-redemptions"
              className={cn(
                "no-underline",
                activeTab === "redemptions" ? "pointer-events-none" : undefined,
              )}
            />
          }
        >
          {t("rewards.redemptions.tab.redemptions")}
          {showBadge ? (
            <Badge
              variant="secondary"
              className="ml-2"
              aria-label={t("rewards.redemptions.tab.pending_count_aria", {
                count: pendingCount,
              })}
              data-testid="rewards-tab-redemptions-pending-count"
            >
              {displayCount}
            </Badge>
          ) : null}
        </TabsTrigger>
      </TabsList>
    </Tabs>
  )
}
